<template>
    <div class="js-accordion translator-accordion" data-testid="accordion-details">
        <h3 class="js-accordion__header translator-accordion__header" data-testid="accordion-details-header"
            :aria-expanded="isOpen" @click="toggleAccordion" @keydown.enter.prevent="toggleAccordion"
            @keydown.space.prevent="toggleAccordion" :class="{ collapsed: !isOpen }"
            style="display: flex; margin-bottom: 0px;">
            {{ messages.accordionDetails }} ({{ filteredDetailItems.length }})
            <svg class="svg-icon svg-angle-down" aria-hidden="true" focusable="false">
                <use xlink:href="/wp-content/themes/ans/design-system/svg-icons/icon-sprite.svg#angle-down"></use>
            </svg>
        </h3>

        <div v-show="isOpen" class="js-accordion__panel translator-accordion__panel">

            <!-- Filtres d'affichage -->
            <div class="mb-3 p-2 bg-light rounded border">
                <p class="fw-bold text-muted small mb-1 text-center">{{ messages.filtersTitle }}</p>
                <div class="d-flex flex-wrap align-items-center gap-3">

                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="filter-synonymes"
                            data-testid="filter-synonymes" v-model="filters.showSynonymes">
                        <label class="custom-control-label small" for="filter-synonymes"
                            style="vertical-align: middle;">{{
                                messages.filterSynonymes }}</label>
                    </div>

                    <div class="custom-control custom-checkbox">
                        <input type="checkbox" class="custom-control-input" id="filter-termes-proches"
                            data-testid="filter-termes-proches" v-model="filters.showTermesProches">
                        <label class="custom-control-label small" for="filter-termes-proches"
                            style="vertical-align: middle;">{{
                                messages.filterTermesProches }}</label>
                    </div>

                    <div class="w-100 d-flex align-items-center flex-wrap gap-2">
                        <span class="mb-0 small fw-semibold text-muted">{{ messages.filterTerminologie }}</span>
                        <div v-for="t in terminologieOptions" :key="t" class="custom-control custom-checkbox">
                            <input type="checkbox" class="custom-control-input" :id="'filter-term-' + t" :value="t"
                                v-model="filters.terminologie">
                            <label class="custom-control-label small" :for="'filter-term-' + t"
                                style="vertical-align: middle;">{{ t }}</label>
                        </div>
                        <button v-if="filters.terminologie.length < terminologieOptions.length" type="button" class="btn rounded-pill py-1 px-3 ml-3"
                            data-testid="filter-terminologie-reset" @click="filters.terminologie = [...terminologieOptions]"
                            :title="messages.filterResetTitle">
                            <i class="bi bi-x-circle me-1" aria-hidden="true"></i>{{ messages.filterResetLabel }}
                        </button>
                    </div>
                </div>
            </div>
            <!-- Résultats -->
            <ul class="list-unstyled mb-0" data-testid="detail-items-list">
                <li v-for="(item, index) in filteredDetailItems" :key="item.term"
                    :data-testid="'detail-item-' + item.term.toLowerCase().replace(/\s+/g, '-')"
                    :class="['mb-4 pb-4', index < filteredDetailItems.length - 1 ? 'border-bottom' : '']">

                    <div class="d-flex align-items-center justify-content-between mb-2 w-100">
                        <strong class="mb-0" style="color: var(--primary);">{{ item.term }}</strong>
                        <span data-testid="display-output-score"
                            :class="['badge', 'badge-pill', badgeClass(item.score)]">
                            {{ formatScore(item.score) }}
                        </span>
                    </div>
                    <dl class="row mb-0">
                        <template v-if="filters.showSynonymes">
                            <dt class="col-sm-4 text-muted fw-normal" style="font-size: 14px;">Synonymes</dt>
                            <dd class="col-sm-8">

                            </dd>
                        </template>
                        <template v-if="filters.showTermesProches">
                            <dt class="col-sm-4 text-muted fw-normal" style="font-size: 14px;">Termes proches</dt>
                            <dd class="col-sm-8">
                                <span v-for="t in getFilteredNearTerms(item)" :key="t"
                                    class="badge bg-light text-dark border me-1">{{ t }}</span>
                            </dd>
                        </template>
                        <template v-if="item.themes && item.themes.length">
                            <dt class="col-sm-4 text-muted fw-normal" style="font-size: 14px;">Concept parent</dt>
                            <dd class="col-sm-8">
                                <span v-for="theme in item.themes" :key="theme"
                                    class="badge bg-light text-dark border me-1">{{ theme }}</span>
                            </dd>
                        </template>
                        <dt class="col-sm-4 text-muted fw-normal" style="font-size: 14px;">Sources de référence</dt>
                        <dd class="col-sm-8">
                            <a v-for="(url, terminology) in getFilteredSources(item)" :key="terminology" :href="url" target="_blank" class="pr-2 source-link"
                                rel="noopener noreferrer" style="font-size: 14px;">
                                {{ terminology }}
                            </a>
                        </dd>
                    </dl>
                </li>
                <li v-if="filteredDetailItems.length === 0" class="text-muted text-center py-2"
                    data-testid="detail-items-empty">
                    <small>{{ messages.noResultsDetails }}</small>
                </li>
            </ul>



        </div>
    </div>
</template>

<script>
import translatorConfig from '../config/translator.config.js';
import { badgeClass, formatScore } from '../utils/translatorUtils.js';

export default {
    name: 'TranslatorDetailsOutput',
    props: {
        detailItems: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            messages: translatorConfig.output.messages,
            isOpen: false,
            filters: {
                showSynonymes: true,
                showTermesProches: true,
                terminologie: []
            }
        }
    },

    computed: {
        terminologieOptions() {
            const sources = this.detailItems
                .flatMap(item => Object.keys(item?.sources || {}))
                .filter(Boolean)
            return [...new Set(sources)].sort((a, b) => a.localeCompare(b, 'fr'))
        },
        filteredDetailItems() {
            if (!this.filters.terminologie.length) return []
            return this.detailItems.filter(item =>
                Object.keys(item.sources || {}).some(t => this.filters.terminologie.includes(t))
            )
        }
    },
    watch: {
        terminologieOptions: {
            immediate: true,
            handler(newOptions) {
                this.filters.terminologie = [...newOptions]
            }
        }
    },
    methods: {
        badgeClass,
        formatScore,
        toggleAccordion() {
            this.isOpen = !this.isOpen
        },
        getFilteredSources(item) {
            if (!this.filters.terminologie.length) return {}
            return Object.fromEntries(
                Object.entries(item.sources || {}).filter(([t]) => this.filters.terminologie.includes(t))
            )
        },
        getFilteredSynonyms(item) {
            if (!this.filters.terminologie.length) return []
            const sourceKeys = Object.keys(item.sources || {})
            return (item.synonymes || []).filter((_, i) => {
                const terminology = sourceKeys[i]
                return terminology && this.filters.terminologie.includes(terminology)
            })
        },
        getFilteredNearTerms(item) {
            if (!this.filters.terminologie.length) return []
            return Object.entries(item.termesProches || {})
                .filter(([t]) => this.filters.terminologie.includes(t))
                .flatMap(([, terms]) => terms)
        },
        extractUrl(src) {
            const match = src && src.match(/https?:\/\/[^\s]+/)
            return match ? match[0] : null
        }
    }
}
</script>

<style scoped>
.custom-control-label.small::before,
.custom-control-label.small::after {
    top: 50%;
    transform: translateY(-50%);
}

dl dd {
    min-width: 0;
    overflow-wrap: break-word;
    word-break: break-word;
}

a.source-link {
    text-decoration: none;
}

a.source-link:hover {
    text-decoration: underline;
}

dl dd .badge {
    white-space: normal;
    text-align: left;
}
</style>
